import { Navigate, createBrowserRouter } from "react-router-dom";
import { LocaleLayout } from "./LocaleLayout";
import { ProtectedLayout } from "./ProtectedLayout";
import { LandingRedirect } from "./LandingRedirect";
import { BackOfficeGate } from "./manage/BackOfficeGate";
import { ManageShell } from "./manage/ManageShell";
import { ManageIndexRedirect } from "./manage/ManageIndexRedirect";
import { LoginPage } from "@/modules/auth/LoginPage";
import { FirstLoginPasswordPage } from "@/modules/auth/FirstLoginPasswordPage";
import { DriverAppLandingPage } from "@/modules/auth/DriverAppLandingPage";
import { DriverInviteOpenPage } from "@/modules/auth/DriverInviteOpenPage";
import { DeliveryTrackingPage } from "@/modules/tracking/DeliveryTrackingPage";
import { TableGridPage } from "@/modules/pos/pages/TableGridPage";
import { InvoicePage } from "@/modules/pos/pages/InvoicePage";
import { TablePage } from "@/modules/pos/pages/TablePage";
import { KitchenBoardPage } from "@/modules/kitchen/pages/KitchenBoardPage";
import { KitchenAvailabilityPage } from "@/modules/kitchen/pages/KitchenAvailabilityPage";
import { StorefrontEditorPage } from "@/modules/manage/pages/settings/StorefrontEditorPage";
import { NotFoundPage, ManageNotFoundPage } from "./NotFoundPage";
import { DEFAULT_LOCALE } from "@/lib/i18n";
import { lazyNamed } from "./lazyPage";

/*
 * Back-office pages load on first visit. They render inside `ManageShell`, whose
 * Suspense boundary shows the LOADING system screen (sidebar stays usable).
 * `StorefrontEditorPage` stays eager: it sits outside the shell, so nothing above
 * it could catch a suspension.
 */
const TodayPage = lazyNamed(
  () => import("@/modules/manage/pages/TodayPage"),
  "TodayPage",
);
const MenuPage = lazyNamed(
  () => import("@/modules/manage/pages/MenuPage"),
  "MenuPage",
);
const MenuItemDetailPage = lazyNamed(
  () => import("@/modules/manage/pages/MenuItemDetailPage"),
  "MenuItemDetailPage",
);
const FloorLegacyRedirect = lazyNamed(
  () => import("@/modules/manage/pages/FloorPage"),
  "FloorLegacyRedirect",
);
const FloorPage = lazyNamed(
  () => import("@/modules/manage/pages/FloorPage"),
  "FloorPage",
);
const AddonGroupsPage = lazyNamed(
  () => import("@/modules/manage/pages/AddonGroupsPage"),
  "AddonGroupsPage",
);
const CategoriesPage = lazyNamed(
  () => import("@/modules/manage/pages/CategoriesPage"),
  "CategoriesPage",
);
const IngredientsPage = lazyNamed(
  () => import("@/modules/manage/pages/IngredientsPage"),
  "IngredientsPage",
);
const ItemCardPage = lazyNamed(
  () => import("@/modules/manage/pages/ItemCardPage"),
  "ItemCardPage",
);
const ReceiveDeliveryPage = lazyNamed(
  () => import("@/modules/manage/pages/ReceiveDeliveryPage"),
  "ReceiveDeliveryPage",
);
const StocktakePage = lazyNamed(
  () => import("@/modules/manage/pages/StocktakePage"),
  "StocktakePage",
);
const AggregatorsPage = lazyNamed(
  () => import("@/modules/manage/pages/AggregatorsPage"),
  "AggregatorsPage",
);
const SuppliersPage = lazyNamed(
  () => import("@/modules/manage/pages/SuppliersPage"),
  "SuppliersPage",
);
const SettingsHubPage = lazyNamed(
  () => import("@/modules/manage/pages/settings/SettingsHubPage"),
  "SettingsHubPage",
);
const GeneralSettingsPage = lazyNamed(
  () => import("@/modules/manage/pages/settings/GeneralSettingsPage"),
  "GeneralSettingsPage",
);
const KitchenSettingsPage = lazyNamed(
  () => import("@/modules/manage/pages/settings/KitchenSettingsPage"),
  "KitchenSettingsPage",
);
const FloorSettingsPage = lazyNamed(
  () => import("@/modules/manage/pages/settings/FloorSettingsPage"),
  "FloorSettingsPage",
);
const PrintingSettingsPage = lazyNamed(
  () => import("@/modules/manage/pages/settings/PrintingSettingsPage"),
  "PrintingSettingsPage",
);
const DeliverySettingsPage = lazyNamed(
  () => import("@/modules/manage/pages/settings/DeliverySettingsPage"),
  "DeliverySettingsPage",
);
const OnlineOrderingSettingsPage = lazyNamed(
  () => import("@/modules/manage/pages/settings/OnlineOrderingSettingsPage"),
  "OnlineOrderingSettingsPage",
);
const SettingsLegacyRedirect = lazyNamed(
  () => import("@/modules/manage/pages/settings/SettingsLegacyRedirect"),
  "SettingsLegacyRedirect",
);
const TaxSettingsPage = lazyNamed(
  () => import("@/modules/manage/pages/settings/TaxSettingsPage"),
  "TaxSettingsPage",
);
const DrawerSettingsPage = lazyNamed(
  () => import("@/modules/manage/pages/settings/DrawerSettingsPage"),
  "DrawerSettingsPage",
);
const PriceTiersPage = lazyNamed(
  () => import("@/modules/manage/pages/settings/PriceTiersPage"),
  "PriceTiersPage",
);
const AppearanceSettingsPage = lazyNamed(
  () => import("@/modules/manage/pages/settings/AppearanceSettingsPage"),
  "AppearanceSettingsPage",
);
const PaymentSettingsPage = lazyNamed(
  () => import("@/modules/manage/pages/settings/PaymentSettingsPage"),
  "PaymentSettingsPage",
);
const StaffPage = lazyNamed(
  () => import("@/modules/manage/pages/StaffPage"),
  "StaffPage",
);
// Driver backend B5 · P18: the drivers roster (each driver's details open in a drawer on it).
const DriversPage = lazyNamed(
  () => import("@/modules/manage/pages/DriversPage"),
  "DriversPage",
);
// P18's details page is now a drawer on the list; its address redirects there.
const DriverDeepLink = lazyNamed(
  () => import("@/modules/manage/pages/DriversPage"),
  "DriverDeepLink",
);
const InvoiceHistoryPage = lazyNamed(
  () => import("@/modules/manage/pages/InvoiceHistoryPage"),
  "InvoiceHistoryPage",
);
const InvoiceDetailPage = lazyNamed(
  () => import("@/modules/manage/pages/InvoiceDetailPage"),
  "InvoiceDetailPage",
);
const ReportsPage = lazyNamed(
  () => import("@/modules/manage/pages/ReportsPage"),
  "ReportsPage",
);
const ItemProfitabilityPage = lazyNamed(
  () => import("@/modules/manage/pages/reports/ItemProfitabilityPage"),
  "ItemProfitabilityPage",
);
const StaffExceptionsPage = lazyNamed(
  () => import("@/modules/manage/pages/reports/StaffExceptionsPage"),
  "StaffExceptionsPage",
);
const DaypartsPage = lazyNamed(
  () => import("@/modules/manage/pages/reports/DaypartsPage"),
  "DaypartsPage",
);
const RemakesPage = lazyNamed(
  () => import("@/modules/manage/pages/reports/RemakesPage"),
  "RemakesPage",
);
const ServiceSpeedPage = lazyNamed(
  () => import("@/modules/manage/pages/reports/ServiceSpeedPage"),
  "ServiceSpeedPage",
);
const RefundsReportPage = lazyNamed(
  () => import("@/modules/manage/pages/reports/RefundsReportPage"),
  "RefundsReportPage",
);
const FeedbackPage = lazyNamed(
  () => import("@/modules/manage/pages/FeedbackPage"),
  "FeedbackPage",
);
const CustomersPage = lazyNamed(
  () => import("@/modules/manage/pages/CustomersPage"),
  "CustomersPage",
);
const InputVatPage = lazyNamed(
  () => import("@/modules/manage/pages/reports/InputVatPage"),
  "InputVatPage",
);
const ShiftPage = lazyNamed(
  () => import("@/modules/manage/pages/ShiftPage"),
  "ShiftPage",
  { width: "form" },
);
const ShiftHistoryPage = lazyNamed(
  () => import("@/modules/manage/pages/ShiftHistoryPage"),
  "ShiftHistoryPage",
);
const DayClosePage = lazyNamed(
  () => import("@/modules/manage/pages/DayClosePage"),
  "DayClosePage",
);

/**
 * Two route trees behind one login: `/pos/**` (the floor) and `/manage/**`
 * (the back office), both under `/:locale`.
 *
 * Locale is in the path rather than in storage so a staff member can be handed
 * a link in the language they read, and so the RTL/LTR direction is decided
 * before the first paint rather than after a hydration flash.
 */
export const router = createBrowserRouter([
  // The ROOT redirect points at the bare locale, NOT at a surface: which
  // surface this member belongs on is a capability question, and
  // `LandingRedirect` is the one place that answers it (see
  // `modules/auth/landing.ts`). An unknown URL is a different question and
  // gets a different answer — see the catch-alls at the bottom (B32).
  { path: "/", element: <Navigate to={`/${DEFAULT_LOCALE}`} replace /> },
  {
    path: "/:locale",
    element: <LocaleLayout />,
    children: [
      { index: true, element: <LandingRedirect /> },
      { path: "login", element: <LoginPage /> },
      { path: "change-password", element: <FirstLoginPasswordPage /> },
      // Driver backend B1: the page a driver's WhatsApp invite link opens.
      // Public — no session, no API call — so outside `ProtectedLayout`.
      { path: "driver-invite/:token", element: <DriverInviteOpenPage /> },
      // Public — the customer's delivery tracking page (driver backend B4 · P16):
      // no session; it reads only the anonymous tracking answer.
      { path: "track/:token", element: <DeliveryTrackingPage /> },
      {
        element: <ProtectedLayout />,
        children: [
          // Driver backend B1: "use the Trioty driver app". `ProtectedLayout`
          // sends a driver-only session here from every route below.
          { path: "driver", element: <DriverAppLandingPage /> },
          { path: "pos", element: <TableGridPage /> },
          { path: "pos/tables/:tableId", element: <TablePage /> },
          { path: "pos/invoices/:invoiceId", element: <InvoicePage /> },
          // The kitchen board, OUTSIDE the back-office shell and outside /pos.
          //
          // Its own top-level tree because its audience is neither of theirs:
          // the KITCHEN role holds no `RESTAURANT_POS_USE` and no `*_MANAGE`
          // permission at all, so a cook rendered inside `ManageShell` would
          // get a sidebar with nothing in it, and inside /pos a floor grid
          // every one of whose calls 403s. It is a third working surface —
          // a screen on a wall — not a page of either app.
          { path: "kitchen", element: <KitchenBoardPage /> },
          // 86'ing, from the same wall tablet (D50). It sits UNDER the board
          // rather than in /manage for the same reason the board does: its
          // audience holds no `*_MANAGE` permission, and the back office would
          // render it inside a sidebar with nothing else in it. Its own
          // permission is `RESTAURANT_MENU_AVAILABILITY`, which the KITCHEN
          // preset now carries and the page checks before it fetches.
          {
            path: "kitchen/availability",
            element: <KitchenAvailabilityPage />,
          },
          /*
           * The storefront EDITOR, outside `ManageShell` on purpose.
           *
           * It is a workbench, not a page of the back office: its own 52px bar
           * carries the store, the device toggle and Publish, and a second bar
           * plus the portal sidebar above that would leave the canvas — the
           * only thing a seller is actually looking at — in a third of the
           * window. Its own top bar has the way back, so nothing is trapped.
           */
          {
            path: "manage/settings/storefront/editor",
            element: <BackOfficeGate><StorefrontEditorPage /></BackOfficeGate>,
          },
          // The back office is a dashboard, so it gets a dashboard shell
          // (sidebar / ☰ drawer, identity, language, logout). The POS routes
          // above deliberately stay outside it: a till is held in one hand and
          // once kept a bottom nav (`AppNav`), a carve-out recorded in
          // docs/design/design-principles.md.
          {
            // Floor roles (a waiter, a cook) never get the shell or its nav:
            // `BackOfficeGate` sends them to their own surface.
            element: <BackOfficeGate><ManageShell /></BackOfficeGate>,
            children: [
              // Bare /manage previously fell through to the "*" catch-all and
              // bounced to the POS — a dead deep-link for the back office. It
              // now lands on the first section the ROLE can see: a hardcoded
              // "menu" target walked cashiers into a permission-denial wall.
              { path: "manage", element: <ManageIndexRedirect /> },
              // TODAY — the home (D111, founder 2026-10-02; supersedes D87).
              // Bare `/manage` lands here for a session that can read one of
              // its management blocks; each block checks its own permission
              // before it fetches, so a typed URL never 403s.
              { path: "manage/today", element: <TodayPage /> },
              { path: "manage/menu", element: <MenuPage /> },
              // Inside the SAME shell, so the detail screen keeps the
              // sidebar/drawer and the top bar still resolves to "Menu"
              // (longest-prefix match in `ManageShell`).
              {
                path: "manage/menu/:productId",
                element: <MenuItemDetailPage />,
              },
              // THE FLOOR — one screen, three views. Live is the default;
              // «UX-round Dn» = the 2026-09-17 UX round's own numbering (`.roundtable/2026-09-17-restaurant-ux/decisions.md`), not §11's.
              // `?view=layout` is the editor and `?view=qr` is the permanent
              // sticker surface; both re-check `FLOOR_MANAGE` because a query
              // string can be typed.
              { path: "manage/floor", element: <FloorPage /> },
              { path: "manage/addons", element: <AddonGroupsPage /> },
              // Menu sections. Seller-API categories, not restaurant ones (D13):
              // the screen needs `CATEGORY_*`, which is why it is nav-gated on
              // `CATEGORY_VIEW` rather than on a `RESTAURANT_*` permission.
              { path: "manage/categories", element: <CategoriesPage /> },
              { path: "manage/ingredients", element: <IngredientsPage /> },
              // The inventory item card — كارت الصنف (D135). Inside the same
              // shell, so the top bar and the sidebar still resolve to Stock
              // (longest-prefix match on `manage/ingredients`). The page checks
              // `COSTING_MANAGE` itself before it fetches.
              {
                path: "manage/ingredients/:id/card",
                element: <ItemCardPage />,
              },
              // The count screen — الجرد (D143). Its own nav row; the page
              // checks `COSTING_MANAGE` before it fetches.
              { path: "manage/stocktake", element: <StocktakePage /> },
              // Receiving a delivery — استلام بضاعة (D147). The page checks
              // `DELIVERY_RECORD` itself before it fetches.
              {
                path: "manage/deliveries/new",
                element: <ReceiveDeliveryPage />,
              },
              // Suppliers (B45, unit 5) — reference data for the purchase
              // form, on the same `COSTING_MANAGE` grant as ingredients.
              { path: "manage/suppliers", element: <SuppliersPage /> },
              { path: "manage/aggregators", element: <AggregatorsPage /> },
              // The floor's OLD second address (UX-round D62). It is kept as a redirect
              // rather than deleted: it was the landing for every `POS_USE`
              // session, so it is in bookmarks, in the QA scripts and in the
              // shot manifest. `replace` so Back does not bounce off it.
              {
                path: "manage/tables",
                element: <FloorLegacyRedirect />,
              },
              // Invoice history is `RESTAURANT_POS_USE`, not a `*_MANAGE`, so
              // waiter and cashier reach it too — unlike every sibling here.
              { path: "manage/invoices", element: <InvoiceHistoryPage /> },
              // Same shell, so the top bar still reads "Invoices" via the
              // longest-prefix match in `ManageShell`.
              {
                path: "manage/invoices/:invoiceId",
                element: <InvoiceDetailPage />,
              },
              // The customer list (2026-10-25), on `CUSTOMER_VIEW`; a
              // customer's details open in a drawer on the list
              // (`?customer=<id>`), never a page of their own.
              { path: "manage/customers", element: <CustomersPage /> },
              // Settings is a HUB, not a page: the landing lists sections and
              // each section is its own route, so a new one is a new entry here
              // rather than another block appended to a screen. All of them sit
              // in the SAME shell, so the top bar still reads "Settings" via the
              // longest-prefix match in `ManageShell`.
              //
              // The eleven sections since the 2026-10-29 regrouping, one topic
              // each (`settingsSections.ts`). Nine write through
              // `PUT /restaurant/settings`, gated server-side on
              // RESTAURANT_SETTINGS_MANAGE, which writes only the columns it is
              // sent — see `settingsSection.tsx` for the read of the mutation
              // that established that (UX-round D53, slice 10.3).
              { path: "manage/settings", element: <SettingsHubPage /> },
              // `general` KEEPS its path: it is the one an owner has bookmarked
              // and the one every capture and doc names.
              {
                path: "manage/settings/general",
                element: <GeneralSettingsPage />,
              },
              // The back-office LOOK. Gated on nothing: it is a per-device
              // preference of whoever is at this screen (see
              // `app/themes/dashboardTheme.ts`), and it writes no store data.
              {
                path: "manage/settings/appearance",
                element: <AppearanceSettingsPage />,
              },
              { path: "manage/settings/tax", element: <TaxSettingsPage /> },
              // Transfers & InstaPay (D44, B35). The only permission pair
              // borrowed from the seller RBAC set: PAYMENT_VIEW reads,
              // PAYMENT_EDIT writes, and the screen checks them apart.
              {
                path: "manage/settings/payment",
                element: <PaymentSettingsPage />,
              },
              // Cash & approvals — keeps the drawer's old path.
              {
                path: "manage/settings/drawer",
                element: <DrawerSettingsPage />,
              },
              // RESTAURANT_TIER_MANAGE, a different permission from the rest,
              // which is why price tiers were split off the old single page.
              { path: "manage/settings/tiers", element: <PriceTiersPage /> },
              // The kitchen: workflow, its targets, and the stations. Station
              // writes are SETTINGS_MANAGE; the page gates before any fetch.
              {
                path: "manage/settings/kitchen",
                element: <KitchenSettingsPage />,
              },
              {
                path: "manage/settings/floor",
                element: <FloorSettingsPage />,
              },
              // The receipt, the ticket, and the printers (all of
              // `restaurant/printing` is SETTINGS_MANAGE).
              {
                path: "manage/settings/printing",
                element: <PrintingSettingsPage />,
              },
              {
                path: "manage/settings/delivery",
                element: <DeliverySettingsPage />,
              },
              // The ordering rules and the store page's look; the editor
              // stays at `settings/storefront/editor`, outside this shell.
              {
                path: "manage/settings/online",
                element: <OnlineOrderingSettingsPage />,
              },
              // The addresses from before the regrouping, each sent to the
              // section that now holds what it held (`SettingsLegacyRedirect`).
              ...(
                [
                  ["operations", "kitchen"],
                  ["service-timing", "kitchen"],
                  ["stations", "kitchen"],
                  ["tax-status", "tax"],
                  ["refunds", "drawer"],
                  ["table-ordering", "floor"],
                  ["storefront", "online"],
                ] as const
              ).map(([from, to]) => ({
                path: `manage/settings/${from}`,
                element: <SettingsLegacyRedirect to={to} />,
              })),
              // OWNER-only in practice: every endpoint behind it requires a
              // MEMBER_* / ROLE_* permission, and no restaurant system role
              // holds one. This is how an owner delegates the back office.
              { path: "manage/staff", element: <StaffPage /> },
              {
                path: "manage/staff/roles",
                element: <StaffPage view="roles" />,
              },
              // Driver backend B5 · P18. The roster (T11) for DISPATCH_MANAGE or
              // DRIVER_DOCUMENTS_VIEW; a driver's details (T12) and, for the
              // documents grant only, the review and access log (T13–T15), in
              // a drawer on the list (`?driver=<sellerId>`). The old details
              // address redirects to the list with that drawer open.
              { path: "manage/staff/drivers", element: <DriversPage /> },
              {
                path: "manage/staff/drivers/:sellerId",
                element: <DriverDeepLink />,
              },
              // The spent-vs-gained report (phase 2). `RESTAURANT_REPORT_VIEW`
              // is its own permission, held by no system role below OWNER.
              { path: "manage/reports", element: <ReportsPage /> },
              // The three tier-2 reports (S1, S3, S4), same shell, same
              // `RESTAURANT_REPORT_VIEW` gate as `manage/reports` above — each
              // is its own screen and its own nav row rather than a tab on
              // the spend-gain page, per E16.
              {
                path: "manage/reports/items",
                element: <ItemProfitabilityPage />,
              },
              {
                path: "manage/reports/staff",
                element: <StaffExceptionsPage />,
              },
              {
                path: "manage/reports/dayparts",
                element: <DaypartsPage />,
              },
              // B117/Q5 — the manager's remake report. Same gate and shell as
              // its siblings: supervision, never the P&L.
              {
                path: "manage/reports/remakes",
                element: <RemakesPage />,
              },
              // B124/D114 — service speed. Supervision: timings, no money.
              {
                path: "manage/reports/speed",
                element: <ServiceSpeedPage />,
              },
              // Founder decision 5 (2026-10-02) — one row per refund. The
              // same supervision gate as the staff report beside it.
              {
                path: "manage/reports/refunds",
                element: <RefundsReportPage />,
              },
              // D134 — what diners said after paying. The same supervision
              // gate as service speed: no money, the manager acts on it.
              {
                path: "manage/feedback",
                element: <FeedbackPage />,
              },
              // The input side of the VAT return (F8, unit 5) — the fourth
              // sibling, same gate and same shell as the three above.
              {
                path: "manage/reports/input-vat",
                element: <InputVatPage />,
              },
              // The cash-drawer shift feature (D23-D29). `SHIFT_MANAGE` for
              // the live screen, `REPORT_VIEW` for the closed-shift review —
              // two different permissions, two routes, same as every other
              // pair in this shell.
              { path: "manage/shift", element: <ShiftPage /> },
              { path: "manage/shifts", element: <ShiftHistoryPage /> },
              // The Z (D38-D41) — a business day, read. `REPORT_VIEW`, and
              // deliberately NOT behind the shift-tracking toggle: block A
              // (the day's sales) stands alone.
              { path: "manage/day-close", element: <DayClosePage /> },
              /*
               * An unknown BACK-OFFICE url (B32), inside this shell on
               * purpose: the sidebar and the drawer stay on screen, so a
               * manager who followed a stale bookmark is one tap from where
               * they meant to go rather than being thrown out of the back
               * office to find it.
               *
               * Last among the shell's children, so every real route above
               * still wins the match — React Router ranks a static path over
               * a splat, but the ordering says the intent too.
               */
              { path: "manage/*", element: <ManageNotFoundPage /> },
            ],
          },
        ],
      },
      /*
       * Everything else unknown under a locale: a mistyped `/pos/...` or
       * `/kitchen/...`, or a path whose first segment is not a locale at all.
       *
       * OUTSIDE `ProtectedLayout`, deliberately. A URL that does not exist
       * does not exist whether or not anyone is logged in, and bouncing a
       * signed-out reader to login only to answer "not found" afterwards
       * teaches them the link might have worked.
       */
      { path: "*", element: <NotFoundPage /> },
    ],
  },
  /*
   * The last resort. Reachable only for a path the locale branch above cannot
   * match at all, and kept as a REDIRECT rather than a screen because there is
   * no locale in hand to render one in — `/{DEFAULT_LOCALE}` then re-enters the
   * tree, where the catch-alls above answer properly.
   */
  { path: "*", element: <Navigate to={`/${DEFAULT_LOCALE}`} replace /> },
]);
